import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import FileUpload from "@/components/FileUpload";
import { Plus, Pencil, Trash2, X, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const POSITIONS = [
    { v: "home_top", l: "Página inicial — topo" },
    { v: "home_middle", l: "Página inicial — meio" },
    { v: "catalog", l: "Catálogo" },
];

const EMPTY = {
    title: "", subtitle: "", cta_text: "", cta_url: "", image_url: "",
    position: "home_top", sort_order: 0, active: true,
};

export default function AdminBanners() {
    const [list, setList] = useState([]);
    const [edit, setEdit] = useState(null);
    const load = () => api.get("/admin/banners").then((r) => setList(r.data));
    useEffect(() => { load(); }, []);

    const save = async (data) => {
        const payload = { ...data, sort_order: Number(data.sort_order || 0) };
        try {
            if (data.id) await api.put(`/admin/banners/${data.id}`, payload);
            else await api.post("/admin/banners", payload);
            toast.success("Banner guardado");
            setEdit(null);
            load();
        } catch (e) {
            toast.error("Erro");
        }
    };
    const toggle = async (b) => {
        await api.put(`/admin/banners/${b.id}`, { ...b, active: !b.active });
        load();
    };
    const del = async (id) => {
        if (!window.confirm("Eliminar banner?")) return;
        await api.delete(`/admin/banners/${id}`);
        load();
    };

    return (
        <div data-testid="admin-banners-page">
            <div className="flex items-center justify-between mb-6">
                <h1 className="font-display font-black text-4xl tracking-tighter">Banners</h1>
                <button onClick={() => setEdit(EMPTY)} className="btn-primary" data-testid="new-banner-btn">
                    <Plus size={16} /> Novo banner
                </button>
            </div>
            <div className="border-2 border-ink bg-white">
                {list.length === 0 ? (
                    <div className="p-10 text-center font-mono text-ink/60">Sem banners. Adicione um para promover destaques.</div>
                ) : (
                    list.map((b, i) => (
                        <div
                            key={b.id}
                            className={`flex gap-4 p-4 ${i < list.length - 1 ? "border-b border-ink/10" : ""}`}
                            data-testid={`banner-${b.id}`}
                        >
                            <div className="w-32 h-20 bg-muted border-2 border-ink shrink-0 overflow-hidden">
                                {b.image_url && <img src={b.image_url} alt="" className="w-full h-full object-cover" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-bold truncate">{b.title}</div>
                                <div className="text-xs font-mono text-ink/60 truncate">{b.subtitle}</div>
                                <div className="text-xs font-mono mt-1">
                                    <span className="badge-edge bg-paper border-ink mr-1">{POSITIONS.find((p) => p.v === b.position)?.l || b.position}</span>
                                    {!b.active && <span className="badge-edge bg-destructive text-white border-destructive">Inactivo</span>}
                                </div>
                            </div>
                            <div className="flex items-start gap-2">
                                <button onClick={() => toggle(b)} className="p-2 hover:text-magenta">
                                    {b.active ? <Eye size={16} /> : <EyeOff size={16} />}
                                </button>
                                <button onClick={() => setEdit(b)} className="p-2 hover:text-magenta">
                                    <Pencil size={16} />
                                </button>
                                <button onClick={() => del(b.id)} className="p-2 hover:text-destructive">
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
            {edit && <BannerModal initial={edit} onClose={() => setEdit(null)} onSave={save} />}
        </div>
    );
}

function BannerModal({ initial, onClose, onSave }) {
    const [f, setF] = useState(initial);
    const upd = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
    return (
        <div className="fixed inset-0 bg-ink/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white border-2 border-ink w-full max-w-2xl shadow-[8px_8px_0_0_#0A0A0A] my-10">
                <div className="p-5 border-b-2 border-ink flex justify-between">
                    <h2 className="font-display font-black text-2xl">{f.id ? "Editar banner" : "Novo banner"}</h2>
                    <button onClick={onClose}><X size={20} /></button>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="p-6 space-y-4">
                    <Field label="Título *" v={f.title} on={upd("title")} required />
                    <Field label="Subtítulo" v={f.subtitle} on={upd("subtitle")} />
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Texto botão" v={f.cta_text} on={upd("cta_text")} />
                        <Field label="URL botão" v={f.cta_url} on={upd("cta_url")} />
                    </div>
                    <div>
                        <span className="label-mono mb-2 block">Imagem</span>
                        <FileUpload value={f.image_url} onChange={(v) => setF((s) => ({ ...s, image_url: v }))} label="" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <label className="block">
                            <span className="label-mono mb-2 block">Posição</span>
                            <select value={f.position} onChange={upd("position")} className="input-edge">
                                {POSITIONS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
                            </select>
                        </label>
                        <Field label="Ordem" type="number" v={f.sort_order} on={upd("sort_order")} />
                    </div>
                    <label className="flex items-center gap-2">
                        <input type="checkbox" checked={!!f.active} onChange={upd("active")} className="accent-magenta w-4 h-4" />
                        <span className="text-sm font-bold uppercase">Activo</span>
                    </label>
                    <div className="flex justify-end gap-3 pt-4 border-t-2 border-ink">
                        <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
                        <button type="submit" className="btn-primary">Guardar</button>
                    </div>
                </form>
            </div>
        </div>
    );
}
function Field({ label, v, on, type = "text", required }) {
    return (
        <label className="block">
            <span className="label-mono mb-2 block">{label}</span>
            <input type={type} value={v || ""} onChange={on} required={required} className="input-edge" />
        </label>
    );
}
